import React, { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowUp, Copy, Eye, EyeOff, Plus, Trash2, Upload } from "lucide-react";
import { rhm, apiError } from "../api";
import { Btn, Card, Field, Input, Select, Textarea } from "../ui";
import { REGISTRY } from "../../site/registry";
import { BAND_ICONS, ICONS } from "../../site/sections/Sections";
import { BadgesField, BlogHighlightField, ColorInput, MediaField } from "./websiteFields2";

// ---------------------------------------------------------------- inspector
export const Inspector = ({ section, products, editable, setProp, setSection }) => {
    const def = REGISTRY[section.type];
    const p = section.props || {};
    return (
        <Card title={`Editing: ${section.name || def?.label}`} data-testid="section-inspector">
            <div className="space-y-4">
                <Field label="Section name" hint={`ID: ${section.id}`}>
                    <Input value={section.name || ""} data-testid="inspector-name"
                        onChange={(e) => setSection({ name: e.target.value })} />
                </Field>
                {(def?.fields || []).map((f) => (
                    <FieldEditor key={f.key} field={f} value={p[f.key]} products={products}
                        onChange={(v) => setProp(f.key, v)} disabled={!editable} />
                ))}
            </div>
        </Card>
    );
};

const BasicField = ({ field, value, onChange, products, disabled }) => {
    const id = `field-${field.key}`;
    if (field.type === "textarea") {
        return <Field label={field.label}><Textarea rows={3} value={value || ""} data-testid={id} disabled={disabled}
            onChange={(e) => onChange(e.target.value)} /></Field>;
    }
    if (field.type === "number") {
        return <Field label={field.label}><Input type="number" value={value ?? 0} data-testid={id} disabled={disabled}
            onChange={(e) => onChange(Number(e.target.value))} /></Field>;
    }
    if (field.type === "px") {
        return (
            <Field label={field.label} hint="Leave blank for auto">
                <Input type="number" min="0" placeholder="auto" data-testid={id} disabled={disabled}
                    value={value === 0 || value ? value : ""}
                    onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} />
            </Field>
        );
    }
    if (field.type === "bool") {
        return <Field label={field.label}><Select value={value === false ? "no" : "yes"} data-testid={id} disabled={disabled}
            onChange={(e) => onChange(e.target.value === "yes")}
            options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]} /></Field>;
    }
    if (field.type === "color") {
        return (
            <Field label={field.label} hint="Leave blank to use the theme default">
                <ColorInput label="" value={value} fallback="#f2f2f7" disabled={disabled}
                    testId={id} onChange={onChange} />
            </Field>
        );
    }
    if (field.type === "select") {
        return <Field label={field.label}><Select value={value || ""} data-testid={id} disabled={disabled}
            onChange={(e) => onChange(e.target.value)} options={field.options} /></Field>;
    }
    if (field.type === "icon") {
        return <Field label={field.label}><Select value={value || "sparkles"} data-testid={id} disabled={disabled}
            onChange={(e) => onChange(e.target.value)} options={Object.keys(ICONS)} /></Field>;
    }
    if (field.type === "band_icon") {
        return (
            <Field label={field.label} hint="Pick the separator shown between the phrases">
                <div className="grid grid-cols-6 gap-1.5" data-testid={id}>
                    <button type="button" disabled={disabled} onClick={() => onChange("")}
                        title="Default sparkle glyph" data-testid="band-icon-default"
                        className={`h-9 flex items-center justify-center border text-sm transition-colors ${
                            !value ? "border-gold text-gold" : "border-white/10 text-zinc-500 hover:text-white"}`}>
                        ✦
                    </button>
                    {Object.entries(BAND_ICONS).map(([key, { label, Icon }]) => (
                        <button key={key} type="button" disabled={disabled} onClick={() => onChange(key)}
                            title={label} data-testid={`band-icon-${key}`}
                            className={`h-9 flex items-center justify-center border transition-colors ${
                                value === key ? "border-gold text-gold" : "border-white/10 text-zinc-500 hover:text-white"}`}>
                            <Icon className="w-4 h-4" />
                        </button>
                    ))}
                </div>
            </Field>
        );
    }
    if (field.type === "product") {
        return <Field label={field.label}><Select value={value || ""} data-testid={id} disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            options={[{ value: "", label: "None" }, ...products.map((pr) => ({ value: pr.slug, label: pr.name }))]} /></Field>;
    }
    if (field.type === "spacing") {
        return <SpacingField field={field} value={value} onChange={onChange} disabled={disabled} />;
    }
    if (field.type === "phrases") {
        return <PhrasesField field={field} value={value} onChange={onChange} disabled={disabled} />;
    }
    if (field.type === "concern") {
        return <ConcernField field={field} value={value} onChange={onChange} disabled={disabled} />;
    }
    if (field.type === "link") return <LinkField field={field} value={value} onChange={onChange} products={products} disabled={disabled} />;
    if (field.type === "image") return <ImageField field={field} value={value} onChange={onChange} disabled={disabled} />;
    return <Field label={field.label}><Input value={value || ""} data-testid={id} disabled={disabled}
        onChange={(e) => onChange(e.target.value)} /></Field>;
};

export const FieldEditor = (props) => {
    const { field, value, onChange, products, disabled } = props;
    if (field.type === "list") return <ListField field={field} value={value || []} onChange={onChange} disabled={disabled} />;
    if (field.type === "badges") return <BadgesField field={field} value={value || []} onChange={onChange} disabled={disabled} />;
    if (field.type === "media") return <MediaField field={field} value={value} onChange={onChange} disabled={disabled} />;
    if (field.type === "blog_highlight") {
        return <BlogHighlightField field={field} value={value || {}} onChange={onChange} disabled={disabled} />;
    }
    if (field.type === "cards") {
        return <CardsField field={field} value={value || []} onChange={onChange} products={products} disabled={disabled} />;
    }
    return <BasicField {...props} />;
};

const SPACING_PRESETS = [
    { label: "DEFAULT", value: "" },
    { label: "TIGHT", value: "16px" },
    { label: "NORMAL", value: "40px" },
    { label: "AIRY", value: "80px" },
    { label: "PULL UP", value: "-32px" },
];

const SpacingField = ({ field, value, onChange, disabled }) => {
    const raw = String(value ?? "");
    const parts = raw.match(/^(-?\d*\.?\d+)(px|%|rem|em|vh|vw)?$/);
    const num = parts ? parts[1] : "";
    const unit = parts && parts[2] ? parts[2] : "px";
    return (
        <Field label={field.label} hint="Pick a quick size, or type your own in px, % or rem. Minus pulls it closer.">
            <div className="space-y-2" data-testid={`field-${field.key}`}>
                <div className="flex flex-wrap gap-1.5">
                    {SPACING_PRESETS.map((preset) => (
                        <button key={preset.label} type="button" disabled={disabled}
                            onClick={() => onChange(preset.value)}
                            data-testid={`${field.key}-preset-${preset.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`mono text-[9px] tracking-[0.14em] px-2.5 py-1.5 border transition-colors ${
                                raw === preset.value
                                    ? "border-gold text-gold bg-gold/10"
                                    : "border-white/10 text-zinc-500 hover:text-white"}`}>
                            {preset.label}
                        </button>
                    ))}
                </div>
                <div className="flex gap-2">
                    <Input type="number" step="1" value={num} placeholder="default" disabled={disabled}
                        data-testid={`${field.key}-amount`}
                        onChange={(e) => onChange(e.target.value === "" ? "" : `${e.target.value}${unit}`)} />
                    <Select className="!w-24" value={unit} disabled={disabled}
                        data-testid={`${field.key}-unit`}
                        options={[{ value: "px", label: "px" }, { value: "%", label: "%" },
                            { value: "rem", label: "rem" }, { value: "vh", label: "vh" }]}
                        onChange={(e) => onChange(num === "" ? "" : `${num}${e.target.value}`)} />
                </div>
            </div>
        </Field>
    );
};

const PhrasesField = ({ field, value, onChange, disabled }) => {
    const rows = (value || []).map((it) => (typeof it === "string" ? { text: it } : it || {}));
    const setRow = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    return (
        <Field label={field.label} hint="Each phrase can carry its own icon — leave it on Default to use the band icon">
            <div className="space-y-2" data-testid={`field-${field.key}`}>
                {rows.map((r, i) => (
                    <div key={`${r.text}-${i}`} className="flex gap-2 items-center">
                        <Input value={r.text || ""} disabled={disabled} data-testid={`phrase-text-${i}`}
                            onChange={(e) => setRow(i, { text: e.target.value })} />
                        <Select className="!w-40" value={r.icon || ""} disabled={disabled}
                            data-testid={`phrase-icon-${i}`}
                            onChange={(e) => setRow(i, { icon: e.target.value })}
                            options={[{ value: "", label: "Default icon" },
                                ...Object.entries(BAND_ICONS).map(([k, v]) => ({ value: k, label: v.label }))]} />
                        <button type="button" disabled={disabled} data-testid={`phrase-delete-${i}`}
                            onClick={() => onChange(rows.filter((_, j) => j !== i))}
                            className="text-zinc-500 hover:text-red-400">
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                ))}
                <Btn variant="ghost" disabled={disabled} data-testid="phrase-add"
                    onClick={() => onChange([...rows, { text: "New phrase", icon: "" }])}>
                    <Plus className="w-3.5 h-3.5 mr-1 inline" /> ADD PHRASE
                </Btn>
            </div>
        </Field>
    );
};

const ConcernField = ({ field, value, onChange, disabled }) => {
    const { data } = useQuery({
        queryKey: ["rhm-concerns"],
        queryFn: async () => (await rhm.get("/concerns")).data,
    });
    const options = data?.options || [];
    return (
        <Field label={field.label} hint="Tag products with this concern in Products → Overview">
            <Select value={value || ""} data-testid={`field-${field.key}`} disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                options={[{ value: "", label: "Choose a concern…" },
                    ...options.map((o) => ({ value: o, label: o }))]} />
        </Field>
    );
};

const LinkField = ({ field, value, onChange, products, disabled }) => (
    <Field label={field.label} hint="Pick a destination or type any URL">
        <div className="space-y-2">
            <Select value="" disabled={disabled} data-testid={`field-${field.key}-picker`}
                onChange={(e) => e.target.value && onChange(e.target.value)}
                options={[
                    { value: "", label: "Quick pick…" },
                    { value: "/", label: "Home" }, { value: "/shop", label: "Shop" },
                    { value: "/consultation", label: "Consultation" }, { value: "/consultation/book", label: "Book consultation" },
                    { value: "/science", label: "Science" }, { value: "/blog", label: "Blog" },
                    { value: "/account", label: "Account" },
                    ...products.map((pr) => ({ value: `/product/${pr.slug}`, label: `Product — ${pr.name}` })),
                ]} />
            <Input value={value || ""} data-testid={`field-${field.key}`} disabled={disabled}
                onChange={(e) => onChange(e.target.value)} placeholder="/shop or https://…" />
        </div>
    </Field>
);

export const ImageField = ({ field, value, onChange, disabled }) => {    const ref = useRef(null);
    const [busy, setBusy] = useState(false);
    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await rhm.post("/site/media?folder=Homepage", fd);
            onChange(data.url);
            toast.success("Image uploaded");
        } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    };
    return (
        <Field label={field.label} hint="Upload a new image or paste a URL">
            <div className="space-y-2">
                {value && <img src={value} alt="" className="w-full h-28 object-cover border border-white/10" />}
                <div className="flex gap-2">
                    <Input value={value || ""} data-testid={`field-${field.key}`} disabled={disabled}
                        onChange={(e) => onChange(e.target.value)} placeholder="https://… or /api/site/media/…" />
                    <Btn variant="ghost" onClick={() => ref.current?.click()} data-testid={`field-${field.key}-upload`}>
                        <Upload className="w-3.5 h-3.5" /> {busy ? "…" : ""}
                    </Btn>
                    {value && <Btn variant="ghost" onClick={() => onChange("")} data-testid={`field-${field.key}-clear`}>
                        <Trash2 className="w-3.5 h-3.5" /></Btn>}
                </div>
                <input ref={ref} type="file" hidden accept=".jpg,.jpeg,.png,.webp,.heic"
                    onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
            </div>
        </Field>
    );
};

export const ListField = ({ field, value, onChange, disabled }) => (    <Field label={field.label}>
        <div className="space-y-2">
            {value.map((v, i) => (
                <div key={i} className="flex gap-2">
                    <Input value={v} data-testid={`field-${field.key}-${i}`} disabled={disabled}
                        onChange={(e) => onChange(value.map((x, j) => (j === i ? e.target.value : x)))} />
                    <Btn variant="ghost" onClick={() => onChange(value.filter((_, j) => j !== i))}
                        data-testid={`field-${field.key}-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></Btn>
                </div>
            ))}
            <Btn variant="ghost" onClick={() => onChange([...value, "New item"])} data-testid={`field-${field.key}-add`}>
                <Plus className="w-3 h-3 mr-1 inline" /> ADD
            </Btn>
        </div>
    </Field>
);

const CardsField = ({ field, value, onChange, products, disabled }) => (
    <Field label={`${field.label} (${value.length})`}>
        <div className="space-y-3">
            {value.map((card, i) => (
                <div key={i} className="border border-white/10 p-3 space-y-2" data-testid={`card-${field.key}-${i}`}>
                    <div className="flex items-center justify-between">
                        <span className="mono text-[9px] tracking-[0.16em] text-zinc-500">CARD {i + 1}</span>
                        <div className="flex gap-1">
                            <button onClick={() => onChange(value.map((c, j) => (j === i ? { ...c, visible: c.visible === false } : c)))}
                                data-testid={`card-${field.key}-visible-${i}`} className="text-zinc-500 hover:text-gold">
                                {card.visible === false ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                            </button>
                            <button onClick={() => { const list = [...value]; list.splice(i + 1, 0, { ...card }); onChange(list); }}
                                data-testid={`card-${field.key}-duplicate-${i}`} className="text-zinc-500 hover:text-white">
                                <Copy className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => onChange(value.filter((_, j) => j !== i))} disabled={disabled}
                                data-testid={`card-${field.key}-remove-${i}`} className="text-zinc-500 hover:text-red-400">
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => { if (i === 0) return; const l = [...value]; [l[i - 1], l[i]] = [l[i], l[i - 1]]; onChange(l); }}
                                data-testid={`card-${field.key}-up-${i}`} className="text-zinc-500 hover:text-white">
                                <ArrowUp className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                    {(field.item || []).map((f) => (
                        <BasicField key={f.key} field={f} value={card[f.key]} products={products} disabled={disabled}
                            onChange={(v) => onChange(value.map((c, j) => (j === i ? { ...c, [f.key]: v } : c)))} />
                    ))}
                </div>
            ))}
            <Btn variant="ghost" onClick={() => onChange([...value, { visible: true, title: "New card", desc: "" }])}
                data-testid={`card-${field.key}-add`}><Plus className="w-3 h-3 mr-1 inline" /> ADD CARD</Btn>
        </div>
    </Field>
);

